import { useEffect, useMemo } from 'react';
import { FilterPickerConsumerProps } from './FilterPickerDynamicConsumer';
import useAccessRights2 from '../../../modules/users/hooks/useAccessRights2';
import useProjectsContext from '../../../modules/projects/hooks/useProjectsContext';
import { syncProjects } from '../../../modules/projects/actions';
import { useDispatch } from 'react-redux';
import { ProjectExtensionType } from '../../../models/ProjectExtension';
import { sortProjects } from '../../../modules/projects/utils';
import useFilterPickerContext from '../hooks/useFilterPickerContext';
import { Project } from '../../../models/Project';

interface ProjectFilterPickerConsumerProps extends FilterPickerConsumerProps {
  extensionRequired?: ProjectExtensionType;
}

const ProjectFilterPickerConsumer: React.FC<
  ProjectFilterPickerConsumerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { children, roles, extensionRequired, parameterName } = props;

  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { checkGlobalRoles, checkOfficeRoles, checkOfficeRolesOfProjectId } =
    useAccessRights2();

  const { allProjects, myProjects } = useProjectsContext();

  const pickerFilterFunction = useFilterPickerContext<Project>(
    parameterName ?? 'project'
  );

  const projects = useMemo(() => {
    if (checkGlobalRoles(['globalAdmin', 'globalController'])) {
      return allProjects;
    }
    if (
      roles.globalRoles.length > 0 &&
      checkGlobalRoles(
        roles.globalRoles.filter((role) => role !== 'globalEmployee')
      )
    ) {
      return allProjects.filter(({ isVisible }) => isVisible);
    }

    if (roles.officeRoles.length > 0 && checkOfficeRoles(roles.officeRoles)) {
      const officesProjects = allProjects.filter((project) =>
        checkOfficeRolesOfProjectId(roles.officeRoles, project.projectId)
      );
      const officesAndMyProjectIds = sortProjects(
        officesProjects.concat(
          myProjects.filter(
            ({ projectId }) =>
              !officesProjects.some(({ projectId: id }) => id === projectId)
          )
        )
      );
      if (checkOfficeRoles(['officeAdmin', 'officeController']))
        return officesAndMyProjectIds;
      return officesAndMyProjectIds.filter(({ isVisible }) => isVisible);
    }
    return myProjects;
  }, [
    roles,
    myProjects,
    allProjects,
    checkGlobalRoles,
    checkOfficeRolesOfProjectId,
    checkOfficeRoles,
  ]);

  const projectsWithRequiredExtensions = useMemo(() => {
    return projects.filter((project) =>
      project.projectExtensionMeDtos?.find(
        (ext) =>
          ext.projectExtensionType === extensionRequired &&
          ext.extensionUserState === 'ready'
      )
    );
  }, [projects, extensionRequired]);

  const _projects = useMemo(() => {
    const _projects = extensionRequired
      ? projectsWithRequiredExtensions
      : projects;
    if (pickerFilterFunction) {
      return _projects.filter(pickerFilterFunction);
    }
    return _projects;
  }, [
    projects,
    projectsWithRequiredExtensions,
    extensionRequired,
    pickerFilterFunction,
  ]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (
      (roles.globalRoles.length > 0 &&
        checkGlobalRoles(
          roles.globalRoles.filter((role) => role !== 'globalEmployee')
        )) ||
      (roles.officeRoles.length > 0 && roles.projectRoles.length > 0)
    ) {
      dispatch(syncProjects());
    }
  }, [roles, checkGlobalRoles, dispatch]);
  //#endregion

  return (
    <>
      {children({
        options: _projects.map(({ projectId, name, number }) => {
          return {
            value: projectId,
            label: `${number} - ${name}`,
            searchValue: `${number} ${name} ${projectId}`,
          };
        }),
      })}
    </>
  );
};

export default ProjectFilterPickerConsumer;
